import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import Joi from 'joi';

import { texts } from 'utils/languages/fr';
import { validateSchema } from 'utils/helpers';
import {
    useCheckVerificationCodeMutation,
    useGetPhoneVerificationCodeMutation,
} from 'api/tunnelSteps';
import { codeVerifierSet } from 'reducers/tunnel';

import { TunnelBox } from 'containers/tunnel/common';
import { SubmitButton, ErrorMessage, CustomTextField, ActionButton } from 'components';
import { Typography } from '@mui/material';

const codeSchema = {
    code: Joi.string().alphanum().length(6).required().messages({
        'string.empty': texts.LE_CODE_N_EST_PAS_VALIDE,
        'string.length': texts.LE_CODE_N_EST_PAS_VALIDE,
        'string.alphanum': texts.LE_CODE_N_EST_PAS_VALIDE,
    }),
};

const SMSCodePage = ({ handleNextStep }) => {
    const dispatch = useDispatch();

    const [getPhoneVerificationCode, { isLoading: smsLoader }] =
        useGetPhoneVerificationCodeMutation();
    const [checkVerificationCode, { isLoading: codeLoader, error, isSuccess, reset }] =
        useCheckVerificationCodeMutation({ fixedCacheKey: 'code-check' });

    const [code, setCode] = useState('');
    const [showInvalidError, setShowInvalidError] = useState(false);
    const [showExpiredError, setShowExpiredError] = useState(false);

    const [codeErrors, setCodeErrors] = useState('');

    const formIsValid = useCallback(() => {
        const { valid, newErrors } = validateSchema(codeSchema, { code });
        setCodeErrors(newErrors?.code || '');

        return valid;
    }, [code]);

    const handleSubmit = useCallback(() => {
        if (formIsValid()) {
            checkVerificationCode({ code });
        }
    }, [checkVerificationCode, code, formIsValid]);

    const handleKeyDown = useCallback(
        e => {
            if (e.key === 'Enter') {
                handleSubmit();
            }
        },
        [handleSubmit]
    );

    const handleResendSMS = useCallback(() => {
        getPhoneVerificationCode();
    }, [getPhoneVerificationCode]);

    useEffect(() => {
        if (error?.data?.message === 'verification_code.not_matching') {
            setShowInvalidError(true);
        } else if (error?.data?.message === 'verification_code.expired') {
            setShowExpiredError(true);
        }
    }, [error]);

    useEffect(() => {
        if (isSuccess) {
            handleNextStep(true);
            dispatch(codeVerifierSet(false));
            reset();
        }
    }, [handleNextStep, isSuccess, dispatch, reset]);

    return (
        <React.Fragment>
            <TunnelBox onKeyDown={handleKeyDown}>
                <Typography align="left" variant="body1" gutterBottom>
                    {texts.VOUS_VENEZ_DE_RECEVOIR_UN_CODE}
                </Typography>
                <CustomTextField
                    formControlProps={{
                        sx: theme => ({
                            width: '100%',
                            maxWidth: theme.constants.tunnelInputWidth,
                        }),
                    }}
                    value={code}
                    onChange={e => setCode(e.target.value.toUpperCase())}
                    error={showInvalidError ? texts.LE_CODE_N_EST_PAS_VALIDE : codeErrors}
                />
                {showExpiredError && (
                    <ErrorMessage
                        align="center"
                        gutterBottom
                        message={texts.LE_CODE_N_EST_PLUS_VALIDE}
                    />
                )}
                {(showExpiredError || showInvalidError) && (
                    <ActionButton
                        onClick={handleResendSMS}
                        sx={{
                            color: 'error.main',
                            '&:hover': {
                                backgroundColor: 'inherit',
                            },
                        }}
                        disabled={smsLoader}
                        loading={smsLoader}
                    >
                        {texts.RENVOYER_LE_CODE}
                    </ActionButton>
                )}
            </TunnelBox>
            <SubmitButton
                text={texts.SUIVANT}
                onClick={handleSubmit}
                disabled={!code || codeLoader}
                loading={codeLoader}
            />
        </React.Fragment>
    );
};

SMSCodePage.propTypes = {
    handleNextStep: PropTypes.func.isRequired,
};

export default SMSCodePage;
